import React, { useState, useCallback, useEffect } from 'react';
import { Analytics } from "@vercel/analytics/react";
import type { View, PromptState, WebsiteData, Plan } from './types';
import { Header } from './components/Header';
import { BuilderForm } from './components/BuilderForm';
import { AIThinkingPlanner } from './components/AIThinkingPlanner';
import { AIPlanner } from './components/AIPlanner';
import { WebsitePreview } from './components/WebsitePreview';
import { LovablePreview } from './components/LovablePreview';
import { ImageEditor } from './components/ImageEditor';
import { ImageGenerator } from './components/ImageGenerator';
import { ImageAnalyzer } from './components/ImageAnalyzer';
import { GeminiChatBot } from './components/GeminiChatBot';
import { LogoDesigner } from './components/LogoDesigner';
import { PlaceFinder } from './components/PlaceFinder';
import { SplashScreen } from './components/SplashScreen';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import { generateWebsitePlan, generateWebsiteContent } from './services/geminiService';
import { saveSnapshot } from './services/historyManager';

type BuilderStage = 'form' | 'planning' | 'generating' | 'preview';

const App: React.FC = () => {
  const [view, setView] = useState<View>('builder');
  const [showSplash, setShowSplash] = useState(true);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('light');

  // Website builder state
  const [builderStage, setBuilderStage] = useState<BuilderStage>('form');
  const [promptState, setPromptState] = useState<PromptState | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [websiteData, setWebsiteData] = useState<WebsiteData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('themeMode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme === 'dark' || (!savedTheme && systemPrefersDark)) {
      setThemeMode('dark');
    } else {
      setThemeMode('light');
    }
    const timer = setTimeout(() => setShowSplash(false), 2500);
    
    // Track visitor on initial load
    trackVisitor();
    
    return () => clearTimeout(timer);
  }, []);

  const trackVisitor = async () => {
    try {
      await fetch('/api/trackVisitor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrer: document.referrer,
          language: navigator.language,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          pathname: window.location.pathname,
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
        }),
      });
    } catch (error) {
      console.error('Failed to track visitor:', error);
    }
  };

  useEffect(() => {
    if (themeMode === 'dark') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('themeMode', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('themeMode', 'light');
    }
  }, [themeMode]);

  const handleToggleTheme = () => {
    setThemeMode(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const handleFormSubmit = useCallback(async (formData: PromptState) => {
    setBuilderStage('planning');
    setIsLoading(true);
    setError(null);
    setPlan(null);
    setWebsiteData(null);
    setPromptState(formData);

    try {
      const generatedPlan = await generateWebsitePlan(formData);
      setPlan(generatedPlan);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred during planning.');
      setBuilderStage('form'); // Go back to form on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleExecutePlan = useCallback(async () => {
    if (!promptState) return;
    setBuilderStage('generating');
    setIsLoading(true);
    setError(null);

    try {
      const generatedData = await generateWebsiteContent(promptState);
      setWebsiteData(generatedData);
      saveSnapshot(generatedData); // Save initial version to history
      setBuilderStage('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred during content generation.');
      setBuilderStage('form'); // Go back to form on error
    } finally {
      setIsLoading(false);
    }
  }, [promptState]);

  const handleResetBuilder = () => {
    setBuilderStage('form');
    setPromptState(null);
    setPlan(null);
    setWebsiteData(null);
    setIsLoading(false);
    setError(null);
  };
  
  const handleWebsiteDataUpdate = (newData: WebsiteData) => {
      setWebsiteData(newData);
      saveSnapshot(newData);
  };

  const handleLoadHistory = (data: WebsiteData) => {
    setWebsiteData(data);
    setBuilderStage('preview');
  };

  const renderView = () => {
    switch (view) {
      case 'builder':
        switch (builderStage) {
          case 'form':
            return <BuilderForm onFormSubmit={handleFormSubmit} onLoadHistory={handleLoadHistory} isLoading={isLoading} error={error} />;
          case 'planning':
            return <AIThinkingPlanner isLoadingPlan={isLoading} plan={plan} onExecutePlan={handleExecutePlan} />;
          case 'generating':
            return <AIPlanner isComplete={!!websiteData} onViewResult={() => setBuilderStage('preview')} />;
          case 'preview':
            return websiteData ? <WebsitePreview initialData={websiteData} onReset={handleResetBuilder} onDataUpdate={handleWebsiteDataUpdate}/> : <div>Loading preview...</div>;
        }
      case 'lovable':
        return <LovablePreview onReset={() => setView('builder')} />;
      case 'imageEditor':
        return <ImageEditor />;
      case 'imageGenerator':
        return <ImageGenerator />;
      case 'imageAnalyzer':
        return <ImageAnalyzer />;
      case 'chat':
        return <GeminiChatBot />;
      case 'logoDesigner':
        return <LogoDesigner />;
      case 'placeFinder':
        return <PlaceFinder />;
      case 'analytics':
        return <AnalyticsDashboard />;
      default:
        return <div>Unknown view</div>;
    }
  };

  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <>
      <div className="bg-brand-cream dark:bg-brand-black min-h-screen font-sans text-brand-charcoal dark:text-white transition-colors duration-300">
        <Header
          currentView={view}
          onViewChange={setView}
          themeMode={themeMode}
          onToggleTheme={handleToggleTheme}
        />
        <main className="container mx-auto px-4 py-8">
          {renderView()}
        </main>
      </div>
      <Analytics />
    </>
  );
};

export default App;